import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import crossIcon from "../../assets/UserIcons/Cancel.svg";

const normalizeValue = (value) => {
  const v = String(value || "").trim().toLowerCase();
  if (v === "like" || v === "dislike") return v;
  return null;
};


const FeedbackButtons = ({ value = null, disabled = false, submitting = false, onSubmit }) => {
  const propValue = useMemo(() => normalizeValue(value), [value]);
  const [localValue, setLocalValue] = useState(propValue);

  const localValueRef = useRef(localValue);
  localValueRef.current = localValue;

  // Parent sync: keep UI aligned with message state updates.
  useEffect(() => {
    setLocalValue(propValue);
  }, [propValue]);

  const [showDislikeModal, setShowDislikeModal] = useState(false);
  const [comment, setComment] = useState("");

  const canInteract = !disabled && !submitting;

  const runSubmit = useCallback(
    async (nextFeedback, nextComment) => {
      if (!canInteract) return;
      if (typeof onSubmit !== "function") return;

      const prev = localValueRef.current;
      try {
        await onSubmit(nextFeedback, nextComment);
      } catch {
        // Revert optimistic UI on failure; parent handler can also toast/retry.
        setLocalValue(prev);
      }
    },
    [canInteract, onSubmit],
  );

  const closeModal = useCallback(() => {
    if (submitting) return;
    setShowDislikeModal(false);
  }, [submitting]);

  const onLikeClick = useCallback(async () => {
    if (!canInteract) return;

    const next = localValueRef.current === "like" ? null : "like";
    setLocalValue(next);
    setShowDislikeModal(false);
    setComment("");

    await runSubmit(next, null);
  }, [canInteract, runSubmit]);

  const onDislikeClick = useCallback(async () => {
    if (!canInteract) return;

    // Toggle off
    if (localValueRef.current === "dislike") {
      setLocalValue(null);
      setShowDislikeModal(false);
      setComment("");
      await runSubmit(null, null);
      return;
    }

    // ChatGPT-like: select instantly, open modal, persist dislike immediately.
    setLocalValue("dislike");
    setShowDislikeModal(true);
    setComment("");
    await runSubmit("dislike", null);
  }, [canInteract, runSubmit]);

  const onDislikeSubmit = useCallback(async () => {
    if (!canInteract) return;
    await runSubmit("dislike", comment.trim() || null);
    setShowDislikeModal(false);
  }, [canInteract, comment, runSubmit]);

  return (
    <div className="feedback-buttons">
      <button
        type="button"
        className={`feedback-btn like ${localValue === "like" ? "active" : ""}`}
        title="Like"
        aria-pressed={localValue === "like"}
        disabled={!canInteract}
        onClick={onLikeClick}
      >
        <i
          className={`bi ${
            localValue === "like" ? "bi-hand-thumbs-up-fill" : "bi-hand-thumbs-up"
          }`}
        />
      </button>

      <button
        type="button"
        className={`feedback-btn dislike ${localValue === "dislike" ? "active" : ""}`}
        title="Dislike"
        aria-pressed={localValue === "dislike"}
        disabled={!canInteract}
        onClick={onDislikeClick}
      >
        <i
          className={`bi ${
            localValue === "dislike"
              ? "bi-hand-thumbs-down-fill"
              : "bi-hand-thumbs-down"
          }`}
        />
      </button>

      {showDislikeModal && (
        <div
          className="modal modal-backdrop-custom d-block"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (submitting) return;
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="modal-history modal-dialog-centered history-model">
            <div className="modal-content">
              <button
                type="button"
                className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                onClick={closeModal}
                aria-label="Close"
              >
                <img src={crossIcon} className="icon-image cross-i" alt="Close" />
              </button>

              <div className="text-center p-3">
                <div
                  className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  <i
                    className="bi bi-hand-thumbs-down"
                    style={{ fontSize: "26px" }}
                  />
                </div>

                <p className="mt-3 mb-2 refresh-prompts fw-bold">What went wrong?</p>

                <textarea
                  className="form-control"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Optional feedback..."
                  rows={3}
                  disabled={!canInteract}
                />
              </div>

              <div className="modal-footer justify-content-center gap-2">
                <button
                  type="button"
                  className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                  disabled={submitting}
                  onClick={closeModal}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                  disabled={submitting}
                  onClick={onDislikeSubmit}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackButtons;
